import React from 'react';
import { Button } from 'antd';
import { ClockCircleFilled, RightOutlined } from '@ant-design/icons';

import { DevPlanActiveSession } from '../types';

type Props = {
  sessions?: DevPlanActiveSession[];
  nodeNameByCode: Record<string, string>;
  onContinue: (session: DevPlanActiveSession) => void;
};

const scenarioLabel = (session: DevPlanActiveSession) =>
  session.blueprint_name ||
  session.blueprint_slug ||
  `Session ${session.session_id}`;

const startedLabel = (value: string | null) => {
  if (!value) return null;
  const started = new Date(value);
  if (Number.isNaN(started.getTime())) return null;
  return started.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * Persistent, server-driven entry point for unfinished sessions. New scenario
 * starts still go through the launch endpoint; this component only continues
 * session IDs explicitly supplied by `/me`.
 *
 * Product terminology: trainee-facing copy says "single player session".
 * "Solo" is reserved for internal action/API identifiers.
 */
const ActiveSessionsBanner: React.FC<Props> = ({
  sessions,
  nodeNameByCode,
  onContinue,
}) => {
  if (!sessions?.length) return null;

  return (
    <section
      className="devplan-active-sessions"
      aria-label="Single player sessions in progress"
    >
      <div className="devplan-active-sessions__intro">
        <ClockCircleFilled aria-hidden />
        <div>
          <strong>
            {sessions.length} single player{' '}
            {sessions.length === 1 ? 'session' : 'sessions'} in progress
          </strong>
          <span>Continue where you left off.</span>
        </div>
      </div>

      <div className="devplan-active-sessions__list">
        {sessions.map(session => {
          const label = scenarioLabel(session);
          const started = startedLabel(session.started_at);
          return (
            <div
              key={session.session_id}
              className="devplan-active-session"
              data-session-id={session.session_id}
            >
              <div className="devplan-active-session__copy">
                <span className="devplan-active-session__name">{label}</span>
                <span className="devplan-active-session__meta">
                  {session.node_name ||
                    nodeNameByCode[session.node_code] ||
                    session.node_code}
                  {started ? ` · started ${started}` : ''}
                </span>
              </div>
              <Button
                size="small"
                type="primary"
                icon={<RightOutlined />}
                onClick={() => onContinue(session)}
                aria-label={`Continue ${label}`}
              >
                Continue
              </Button>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default ActiveSessionsBanner;
